import { t } from "i18next";
import { create } from "zustand";
import { toast } from "@/hooks/use-toast";
import { downloadAudio } from "@/lib/download-audio";
import { heldRecordingIds } from "@/lib/offline-recording/capture";
import {
  deleteRecording,
  listRecordings,
  readRecordingAudio,
  updateRecording,
} from "@/lib/offline-recording/db";
import {
  canAccessOfflineRecording,
  hasPublicRecordingIdentity,
  isPublicVisitForm,
  offlineIdentityUpgrade,
} from "@/lib/offline-recording/identity";
import {
  readStorageStatus,
  type StorageStatus,
} from "@/lib/offline-recording/limits";
import {
  enqueueRecording,
  isRecoverable,
  OfflineUploadQueue,
  purgeExpiredRecordings,
} from "@/lib/offline-recording/queue";
import type {
  OfflineRecording,
  OfflineVisitForm,
} from "@/lib/offline-recording/types";
import { getVisitScope } from "@/lib/visit-queries";
import { useAuthStore } from "./auth.store";
import { usePostOnboardingStore } from "./postOnboarding.store";

/** The signed-in account (same identity as the visit read scope). */
export const currentOfflineAccount = getVisitScope;
const currentAccount = currentOfflineAccount;

/** Refreshes the visit list and usage after the server accepted a recording. */
const refreshVisitsAfterUpload = async (
  expected = getVisitScope(),
): Promise<void> => {
  // Loaded on use so this store does not pull the query layer into every page.
  const [{ getVisitScope, refreshVisitReads }, { refreshOwnUsage }] =
    await Promise.all([
      import("@/lib/visit-queries"),
      import("@/lib/usage-queries"),
    ]);
  const scope = getVisitScope();
  if (scope !== expected) return;
  void refreshVisitReads(scope);
  if (scope) void refreshOwnUsage(scope);
};

const browserOnline = (): boolean =>
  typeof navigator === "undefined" || navigator.onLine !== false;

export interface OfflineRecordingState {
  /** This account's recordings on this device, oldest first. */
  recordings: OfflineRecording[];
  /** Recordings a live tab holds (Web Locks), null if unknown. */
  held: Set<string> | null;
  progress: Record<string, number>;
  online: boolean;
  storage: StorageStatus | null;
  /** The recovery dialog was closed with "Later" on this page. */
  recoveryDismissed: boolean;
  /** Recordings being processed from the recovery dialog. */
  processingId: string | null;
  loaded: boolean;
  error: string | null;
  init: () => void;
  refresh: () => Promise<void>;
  enqueue: (id: string, form: OfflineVisitForm) => Promise<void>;
  retry: (id: string) => Promise<void>;
  discard: (id: string) => Promise<void>;
  /** Removes every recording of the signed-in account (sign-out choice). */
  discardAll: () => Promise<void>;
  download: (id: string) => Promise<void>;
  markSubmitAttempted: (id: string) => Promise<void>;
  /** The server confirmed a live-transcript visit: drop the local copy. */
  confirmed: (id: string) => Promise<void>;
  setProcessing: (id: string | null) => void;
  dismissRecovery: () => void;
}

let queue: OfflineUploadQueue | null = null;
let started = false;

/** Recordings to offer in the recovery dialog. */
export const recoverableRecordings = (
  recordings: OfflineRecording[],
  held: Set<string> | null = null,
  now = Date.now(),
): OfflineRecording[] =>
  recordings
    .filter((recording) => isRecoverable(recording, now, held))
    .sort((a, b) => b.createdAt - a.createdAt);

/** Recordings that are waiting for, or failed, an upload. */
export const queuedRecordings = (
  recordings: OfflineRecording[],
): OfflineRecording[] =>
  recordings.filter(
    (recording) =>
      recording.status === "queued" ||
      recording.status === "uploading" ||
      recording.status === "failed" ||
      recording.status === "uncertain",
  );

export const useOfflineRecordingStore = create<OfflineRecordingState>()(
  (set, get) => {
    const getQueue = (): OfflineUploadQueue => {
      queue ??= new OfflineUploadQueue({
        account: currentAccount,
        online: browserOnline,
        onChange: () => {
          void get().refresh();
        },
        onProgress: (id, percent) => {
          set({ progress: { ...get().progress, [id]: percent } });
        },
        onConfirmed: () => {
          void refreshVisitsAfterUpload().catch(() => undefined);
          usePostOnboardingStore.getState().markDone();
          toast({
            variant: "success",
            title: t("offlineRecording.uploaded.title", "Recording uploaded"),
            description: t(
              "offlineRecording.uploaded.description",
              "The visit is being processed and the copy on this device was removed.",
            ),
          });
        },
      });
      return queue;
    };

    return {
      recordings: [],
      held: null,
      progress: {},
      online: browserOnline(),
      storage: null,
      recoveryDismissed: false,
      processingId: null,
      loaded: false,
      error: null,
      init: () => {
        if (started) return;
        started = true;
        set({ online: browserOnline() });
        const onOnline = (): void => {
          set({ online: true });
          void getQueue().wake();
        };
        const onOffline = (): void => set({ online: false });
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        const key = (): string | null => {
          const value = currentAccount();
          return value ? `${value.uid}:${value.accountPublicId}` : null;
        };
        let account = key();
        useAuthStore.subscribe(() => {
          const next = key();
          if (next === account) return;
          account = next;
          set({
            recordings: [],
            progress: {},
            loaded: false,
            recoveryDismissed: false,
            processingId: null,
          });
          void get().refresh();
          void getQueue().drain();
        });
        void (async () => {
          try {
            const removed = await purgeExpiredRecordings();
            if (removed.length)
              toast({
                title: t(
                  "offlineRecording.expired.title",
                  "Old recordings removed",
                ),
                description: t(
                  "offlineRecording.expired.description",
                  "{{count}} recording(s) older than 7 days were deleted from this device.",
                  { count: removed.length },
                ),
              });
          } catch {}
          set({ storage: await readStorageStatus() });
          await get().refresh();
          await getQueue().drain();
        })();
        // TTL clean-up for tabs that stay open for days.
        setInterval(
          () => {
            void purgeExpiredRecordings()
              .then(() => get().refresh())
              .catch(() => undefined);
          },
          60 * 60 * 1000,
        );
      },
      refresh: async () => {
        const account = currentAccount();
        if (!account) {
          set({ recordings: [], loaded: true });
          return;
        }
        try {
          const [rows, held] = await Promise.all([
            listRecordings(),
            heldRecordingIds(),
          ]);
          const recordings = rows
            .filter((recording) =>
              canAccessOfflineRecording(recording, account),
            )
            .sort((a, b) => a.createdAt - b.createdAt);
          if (currentAccount() !== account) return;
          set({ recordings, held, loaded: true, error: null });
        } catch (error) {
          if (currentAccount() !== account) return;
          set({
            loaded: true,
            error:
              error instanceof Error
                ? error.message
                : t("offlineRecording.errors.unreadable"),
          });
        }
      },
      enqueue: async (id, form) => {
        const account = currentAccount();
        if (!account || !isPublicVisitForm(form))
          throw Error(offlineIdentityUpgrade());
        await updateRecording(id, (current) => {
          if (
            currentAccount() !== account ||
            !canAccessOfflineRecording(current, account)
          )
            throw Error("Account changed before recording submission");
          if (hasPublicRecordingIdentity(current)) return null;
          // Explicit form submission can reselect an unsubmitted old recording.
          // Existing requests may already have reached the server: never replay them.
          if (current.upload || current.submitAttempted)
            throw Error(
              `${offlineIdentityUpgrade()} ${t("offlineRecording.errors.discardOldRequest")}`,
            );
          const { accountId: _oldAccountId, ...retained } = current;
          return {
            ...retained,
            version: 2,
            account: `${account.uid}:${account.accountPublicId}`,
            accountPublicId: account.accountPublicId,
            form,
          };
        });
        if (currentAccount() !== account) return;
        await enqueueRecording(id, form);
        await get().refresh();
        void getQueue().drain();
      },
      retry: async (id) => {
        await getQueue().retry(id);
        await get().refresh();
      },
      discard: async (id) => {
        await getQueue().discard(id);
        await get().refresh();
      },
      discardAll: async () => {
        for (const recording of get().recordings)
          await getQueue().discard(recording.id);
        await get().refresh();
      },
      download: async (id) => {
        const recording = get().recordings.find((row) => row.id === id);
        if (!recording) return;
        const audio = await readRecordingAudio(id, recording.mimeType);
        await downloadAudio(audio);
      },
      markSubmitAttempted: async (id) => {
        const account = currentAccount();
        await updateRecording(id, (current) =>
          account &&
          currentAccount() === account &&
          canAccessOfflineRecording(current, account)
            ? {
                ...current,
                submitAttempted: true,
              }
            : null,
        ).catch(() => undefined);
      },
      confirmed: async (id) => {
        const account = currentAccount();
        const row = get().recordings.find((value) => value.id === id);
        if (
          !account ||
          !row ||
          !hasPublicRecordingIdentity(row) ||
          !canAccessOfflineRecording(row, account)
        )
          return;
        await deleteRecording(id).catch(() => undefined);
        await get().refresh();
      },
      setProcessing: (id) => set({ processingId: id }),
      dismissRecovery: () => set({ recoveryDismissed: true }),
    };
  },
);

/** Test helper: forget the singleton queue and listeners state. */
export const resetOfflineRecordingStoreForTests = (): void => {
  queue?.dispose();
  queue = null;
  started = false;
  useOfflineRecordingStore.setState({
    recordings: [],
    held: null,
    progress: {},
    online: true,
    storage: null,
    recoveryDismissed: false,
    processingId: null,
    loaded: false,
    error: null,
  });
};
